"use client";

import { FlightItinerary } from "./itinerary-blocks/flight-itinerary";
import { AttractionTicketItinerary } from "./itinerary-blocks/attraction-ticket-itinerary";
import { CarHireItinerary } from "./itinerary-blocks/car-hire-itinerary";
import { CruiseItinerary } from "./itinerary-blocks/cruise-itinerary";
import { ExcursionItinerary } from "./itinerary-blocks/excursion-itinerary";
import { HotelItinerary } from "./itinerary-blocks/hotel-itinerary";
import { TransferItinerary } from "./itinerary-blocks/transfer-itinerary";
import { RailItinerary } from "./itinerary-blocks/rail-itinerary";

import { useQuoteThemeStyles } from "./quote-theme/use-quote-theme-styles";
import { iHolidayQuoteItineraryBlock } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { GlobalErrorBoundary } from "@/error-boundary";
import { BlockErrorFallback } from "../block-error-fallback";

interface ItinerarySectionProps {
  itinerary: iHolidayQuoteItineraryBlock[];
  primaryColor?: string;
}

export function ItinerarySection({
  itinerary,
}: Omit<ItinerarySectionProps, "primaryColor">) {
  const { sectionHeadingClassName, classNames } = useQuoteThemeStyles();

  return (
    <section className={`${classNames.pageBackground} p-4 md:p-6 rounded-3xl`}>
      <h2 className={`${sectionHeadingClassName} mb-6`}>Your Itinerary</h2>
      <div className="space-y-6">
        {itinerary.map((item, index) => (
          <div
            key={item.id}
            id={`itinerary-${item.id}`}
            className="scroll-mt-24"
          >
            <ItineraryBlock item={item} index={index} />
          </div>
        ))}
      </div>
    </section>
  );
}

export const ItineraryBlock = ({
  item,
  index,
}: {
  item: iHolidayQuoteItineraryBlock;
  index: number;
}) => {
  return (
    <GlobalErrorBoundary FallbackComponent={BlockErrorFallback} data={item}>
      <_ItineraryBlock item={item} index={index} />
    </GlobalErrorBoundary>
  );
};

const _ItineraryBlock = ({
  item,
  index,
}: {
  item: iHolidayQuoteItineraryBlock;
  index: number;
}) => {
  let content;

  switch (item.type) {
    case "flight":
      content = (
        <FlightItinerary key={index} {...item.flightData!} />
      );
      break;
    case "hotel":
      content = (
        <HotelItinerary
          key={index}
          {...item.accommodationData!}
          day={index + 1}
        />
      );
      break;
    case "cruise":
      content = (
        <CruiseItinerary
          key={index}
          {...item.cruiseData!}
          day={index + 1}
        />
      );
      break;
    case "car":
      content = (
        <CarHireItinerary
          key={index}
          {...item.carHireData!}
          day={index + 1}
        />
      );
      break;
    case "transfer":
      content = (
        <TransferItinerary
          key={index}
          {...item.transferData!}
          day={index + 1}
        />
      );
      break;
    case "attraction":
      content = (
        <AttractionTicketItinerary
          key={index}
          {...item.attractionData!}
          day={index + 1}
        />
      );
      break;
    case "excursion":
      content = (
        <ExcursionItinerary
          key={index}
          {...item.excursionData!}
          day={index + 1}
        />
      );
      break;
    case "rail":
      content = (
        <RailItinerary
          key={index}
          {...item.railData!}
          day={index + 1}
        />
      );
      break;
  }

  return content;
};
